/**
 * PromLiveScreen — /prom/:slug/live
 *
 * The projector view. Renders the current reveal phase by polling
 * /api/events/:id/reveal-status every 2 seconds. Designed to be
 * cast/fullscreened in front of a room — no UI controls, just a
 * single dramatic surface that auto-progresses through:
 *
 *   live → locked → countdown → reveal
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Crown, LockKey, Sparkle, Trophy } from '@phosphor-icons/react';
import UpsellBanner from '../components/UpsellBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 2000;

export default function PromLiveScreen() {
  const { slug } = useParams();
  const [status, setStatus] = useState(null);
  const [liveEntries, setLiveEntries] = useState([]);
  const [error, setError] = useState('');
  const eventIdRef = useRef(null);
  const challengeIdRef = useRef(null);

  // Resolve event_id once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ev = await axios.get(`${API}/events/slug/${slug}`);
        if (cancelled) return;
        eventIdRef.current = ev.data.event_id;
      } catch (e) {
        if (!cancelled) setError('Could not load event.');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Poll status every 2s
  useEffect(() => {
    let cancelled = false;
    let timer;
    const tick = async () => {
      if (!eventIdRef.current) {
        timer = setTimeout(tick, POLL_MS);
        return;
      }
      try {
        const res = await axios.get(`${API}/events/${eventIdRef.current}/reveal-status`);
        if (cancelled) return;
        setStatus(res.data);
        // For LIVE phase, fetch the leaderboard of the resolved challenge
        // (or the most-voted spotlight if none set yet) so the projector
        // can show the live top 3.
        const cid = res.data.challenge_id;
        if (res.data.phase === 'live' && cid && cid !== challengeIdRef.current) {
          challengeIdRef.current = cid;
        }
        if (res.data.phase === 'live' && cid) {
          try {
            const lb = await axios.get(`${API}/challenges/${cid}/leaderboard`);
            if (!cancelled) setLiveEntries((lb.data?.entries || []).slice(0, 3));
          } catch (e) { /* ignore */ }
        }
      } catch (e) { /* ignore single-poll failures */ }
      finally {
        if (!cancelled) timer = setTimeout(tick, POLL_MS);
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  if (error) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center text-xl" data-testid="prom-live-error">{error}</div>;
  }
  if (!status) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="prom-live-loading">
        <Sparkle size={36} weight="fill" className="text-[#C29D52] animate-pulse" />
      </div>
    );
  }

  const { phase, seconds_remaining, school_name, challenge_title, challenge_category, top_entries } = status;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 py-10 text-center overflow-hidden" data-testid="prom-live-screen">
      <AnimatePresence mode="wait">
        {phase === 'live' && (
          <motion.div key="live" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full max-w-3xl">
            <p className="text-amber-300 text-sm font-black uppercase tracking-[0.3em] mb-3 flex items-center justify-center gap-2" data-testid="prom-live-phase-live">
              <span className="w-2 h-2 rounded-full bg-amber-300 animate-pulse" /> Voting live
            </p>
            <h1 className="text-4xl sm:text-6xl font-black mb-2">{challenge_title || 'Prom Challenge'}</h1>
            {school_name && <p className="text-white/55 text-lg mb-8">{school_name}</p>}
            <div className="space-y-3 max-w-xl mx-auto">
              {liveEntries.length === 0 ? (
                <p className="text-white/45 text-base">Voting is open — top 3 will appear here</p>
              ) : (
                liveEntries.map((e) => (
                  <div key={e.entry_id} className="flex items-center gap-4 rounded-2xl bg-white/[0.04] border border-white/10 p-4">
                    <div className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-lg font-black ${
                      e.rank === 1 ? 'bg-amber-300/25 text-amber-200'
                      : e.rank === 2 ? 'bg-white/15 text-white/85'
                      : 'bg-orange-400/20 text-orange-200'
                    }`}>
                      {e.rank === 1 ? <Crown size={20} weight="fill" /> : `#${e.rank}`}
                    </div>
                    {e.media_url && (
                      e.type === 'video'
                        ? <video src={e.media_url} className="w-14 h-14 rounded-xl object-cover bg-neutral-900" muted />
                        : <img src={e.media_url} alt="" className="w-14 h-14 rounded-xl object-cover bg-neutral-900" />
                    )}
                    <div className="flex-1 text-left">
                      <p className="text-2xl font-black">Entry #{e.rank}</p>
                      <p className="text-white/55 text-sm">{e.votes} vote{e.votes === 1 ? '' : 's'}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}

        {phase === 'locked' && (
          <motion.div key="locked" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="text-center">
            <LockKey size={56} weight="fill" className="text-white/55 mx-auto mb-6" />
            <h1 className="text-5xl sm:text-7xl font-black mb-4" data-testid="prom-live-phase-locked">Voting closed</h1>
            <p className="text-2xl text-white/65">Final votes are in…</p>
          </motion.div>
        )}

        {phase === 'countdown' && (
          <motion.div key="countdown" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center">
            <p className="text-[#C29D52] text-base sm:text-xl font-black uppercase tracking-[0.3em] mb-6">
              👑 Announcement in
            </p>
            <motion.div
              key={seconds_remaining}
              initial={{ scale: 1.2, opacity: 0.6 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.45 }}
              className="text-[160px] sm:text-[240px] font-black leading-none tabular-nums"
              style={{ textShadow: '0 0 60px rgba(194,157,82,0.4)' }}
              data-testid="prom-live-countdown"
            >
              {String(Math.max(0, seconds_remaining)).padStart(2, '0')}
            </motion.div>
            <p className="text-white/65 text-xl sm:text-2xl mt-6">Get ready 👀</p>
          </motion.div>
        )}

        {phase === 'reveal' && (
          <Reveal slug={slug} title={challenge_title} category={challenge_category} entries={top_entries || []} />
        )}
      </AnimatePresence>
    </div>
  );
}

function Reveal({ slug, title, category, entries }) {
  // Tiny "drumroll" beat before the winner glow lands.
  const [stage, setStage] = useState('drumroll');
  useEffect(() => {
    const t = setTimeout(() => setStage('winner'), 1500);
    return () => clearTimeout(t);
  }, []);

  const isProm = category === 'spotlight';
  const headline = isProm
    ? '🎓 Prom Spotlight'
    : '🏆 Challenge Winner';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center w-full max-w-3xl">
      <p className="text-amber-300 text-sm sm:text-base font-black uppercase tracking-[0.3em] mb-3">{headline}</p>
      <h2 className="text-2xl sm:text-3xl text-white/85 mb-6">{title}</h2>

      {stage === 'drumroll' && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl sm:text-5xl font-black text-white/55"
          data-testid="prom-live-drumroll"
        >
          And the winner is…
        </motion.div>
      )}

      {stage === 'winner' && (
        <>
          {entries[0] ? (
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.7, ease: [0.25, 1.5, 0.5, 1] }}
              className="mb-10"
              data-testid="prom-live-winner"
            >
              <div className="flex items-center justify-center gap-3 mb-6">
                <Crown size={40} weight="fill" className="text-amber-300" />
                <p className="text-xl text-white/65">#1</p>
                <Crown size={40} weight="fill" className="text-amber-300" />
              </div>
              {entries[0].media_url && (
                entries[0].type === 'video'
                  ? <video src={entries[0].media_url} className="w-56 h-56 sm:w-72 sm:h-72 rounded-3xl object-cover mx-auto mb-6 shadow-[0_0_120px_rgba(255,193,7,0.45)]" autoPlay loop muted />
                  : <img src={entries[0].media_url} alt="Winner" className="w-56 h-56 sm:w-72 sm:h-72 rounded-3xl object-cover mx-auto mb-6 shadow-[0_0_120px_rgba(255,193,7,0.45)]" />
              )}
              <p className="text-5xl sm:text-7xl font-black bg-gradient-to-r from-amber-200 via-amber-300 to-amber-200 bg-clip-text text-transparent">
                {entries[0].votes} vote{entries[0].votes === 1 ? '' : 's'}
              </p>
            </motion.div>
          ) : (
            <p className="text-2xl text-white/55 mb-10">No entries yet — try again later</p>
          )}

          {entries.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="grid grid-cols-3 gap-4 max-w-2xl mx-auto"
              data-testid="prom-live-podium"
            >
              {[0, 1, 2].map((i) => {
                const e = entries[i];
                const medal = ['🥇', '🥈', '🥉'][i];
                return (
                  <div key={i} className="rounded-2xl bg-white/[0.04] border border-white/10 p-4">
                    <p className="text-3xl sm:text-4xl mb-2">{medal}</p>
                    <p className="text-sm sm:text-base font-bold">{e ? `Entry #${e.rank}` : '—'}</p>
                    <p className="text-[12px] text-white/55">{e ? `${e.votes} vote${e.votes === 1 ? '' : 's'}` : ''}</p>
                  </div>
                );
              })}
            </motion.div>
          )}

          <Confetti />

          {/* Phone re-entry — pulls the room back into their pockets */}
          <PhoneReEntryCard slug={slug} />

          {/* Upsell — peak emotional state, "I want this for my event" */}
          <UpsellBanner
            source="prom_live_reveal"
            variant="live"
            headline="Want a moment like this for your event?"
            sub="Birthday · house party · uni night — create yours in 30 seconds."
            cta="Create my event"
          />
        </>
      )}
    </motion.div>
  );
}

function PhoneReEntryCard({ slug }) {
  const url = typeof window !== 'undefined' ? `${window.location.origin}/e/${slug}?reveal=1` : '';
  const qrSrc = `${API}/prom/${slug}/qr`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.9, duration: 0.5 }}
      className="mt-10 max-w-xl mx-auto rounded-3xl bg-white/[0.04] border border-white/12 p-6 sm:p-7"
      data-testid="prom-live-phone-reentry"
    >
      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#C29D52] mb-3">📱 See full results on your phone</p>
      <div className="flex items-center gap-5">
        <img src={qrSrc} alt="Scan to open on your phone" className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl bg-white p-2 shrink-0" />
        <div className="text-left flex-1 min-w-0">
          <p className="text-lg sm:text-xl font-black mb-1 leading-tight">Scan to relive the night</p>
          <p className="text-white/60 text-sm break-all">{url.replace('https://', '').replace('http://', '')}</p>
        </div>
      </div>
    </motion.div>
  );
}

// Lightweight CSS-confetti — no extra deps, won't tank the projector
function Confetti() {
  const pieces = Array.from({ length: 60 });
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.8;
        const duration = 2 + Math.random() * 2;
        const colours = ['#fbbf24', '#C29D52', '#B89762', '#f472b6', '#34d399'];
        const color = colours[i % colours.length];
        return (
          <span
            key={i}
            style={{
              position: 'absolute',
              top: '-10px',
              left: `${left}%`,
              width: '10px',
              height: '14px',
              background: color,
              borderRadius: '2px',
              transform: 'rotate(45deg)',
              animation: `prom-fall ${duration}s ${delay}s linear forwards`,
            }}
          />
        );
      })}
      <style>{`
        @keyframes prom-fall {
          to { transform: translateY(110vh) rotate(720deg); opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
